import { Result } from "../../../../api/types";
import type { AggregatedQto, AggregatedQuantitySlot } from "./types";
import { handleError } from "../../../../errors";
import {
  TogalSession,
  TogalProject,
  TogalSet,
  TogalSetDetails,
  TogalClassification,
  TogalPaginatedResponse,
  TogalPage,
  TogalViewDetail,
} from "./types";
import {
  buildClassificationQuantitySlots,
  classificationCustomOrderId,
  classificationMayHaveIncompleteQuantities,
  convertInternalQuantityToDisplayUom,
  featureHasUsableGeometry,
  inferMeasureFamily,
  pickViewMultiplier,
  PREDEFINED_GROUP_INCOMPLETE_REASON,
  resolveFeatureQuantity,
  shouldIncludeViewForAggregation,
  type ClassificationQuantitySlot,
} from "./utils";
import { ediphiProductIdFromClassificationEdiphiId } from "../../utils";
import { browserStore } from "../../../../utils/storageKeys";

/** Togal login email, kept across logout so the login form and the source header can both read it. */
export const TOGAL_LOGIN_USERNAME_KEY = "togalLoginUsername";

const TOGAL_API_BASE_URL = "https://www-prod.togal.ai";

export const getTogalProjectUrl = (organizationId: string, projectId: string): string =>
  `${TOGAL_API_BASE_URL}/dashboard/projects/${organizationId}/${projectId}/detail`;

const togalFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
  const sessionId = browserStore.get("togalSessionId");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (sessionId) {
    headers["session"] = sessionId;
  }

  return fetch(`${TOGAL_API_BASE_URL}${path}`, {
    ...options,
    headers,
  });
};

export const togalLogin = async (email: string, password: string): Promise<Result<TogalSession>> => {
  try {
    const response = await togalFetch("/api/v1/session", {
      method: "POST",
      body: JSON.stringify({ email, password, remember: true }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const apiMessage = response.status === 404 ? "Invalid email or password" : errorBody.message || "Login failed";
      const rawErr = Object.assign(new Error(apiMessage), { statusCode: response.status });
      return { success: false, error: handleError(rawErr, { context: "togalLogin", status: response.status }) };
    }

    const session: TogalSession = await response.json();
    browserStore.set("togalSessionId", session.id);
    if (session.organization_id) {
      browserStore.set("togalOrganizationId", session.organization_id);
    } else {
      browserStore.remove("togalOrganizationId");
    }
    browserStore.set(
      "togalUserProfile",
      JSON.stringify({
        user_id: session.user_id,
        organization_id: session.organization_id,
      })
    );

    return { success: true, data: session };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { context: "togalLogin" }),
    };
  }
};

export const togalLogout = async (): Promise<Result> => {
  try {
    await togalFetch("/api/v1/session", { method: "DELETE" });
    browserStore.remove("togalSessionId");
    browserStore.remove("togalOrganizationId");
    browserStore.remove("togalUserProfile");
    return { success: true };
  } catch (error) {
    browserStore.remove("togalSessionId");
    browserStore.remove("togalOrganizationId");
    browserStore.remove("togalUserProfile");
    return {
      success: false,
      error: handleError(error, { context: "togalLogout" }),
    };
  }
};

export const togalValidateSession = async (): Promise<boolean> => {
  try {
    const sessionId = browserStore.get("togalSessionId");
    if (!sessionId) return false;

    const response = await togalFetch("/api/v1/project");
    return response.ok;
  } catch {
    return false;
  }
};

const TOGAL_PAGE_LIMIT = 100;

export const fetchTogalProjects = async (): Promise<Result<TogalProject[]>> => {
  const allRows: TogalProject[] = [];
  let offset = 0;

  try {
    while (true) {
      const params = new URLSearchParams({
        $offset: String(offset),
        $limit: String(TOGAL_PAGE_LIMIT),
      });
      const response = await togalFetch(`/api/v1/project?${params}`);

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const rawErr = Object.assign(new Error(errorBody.message || "Failed to fetch projects"), {
          statusCode: response.status,
        });
        return {
          success: false,
          error: handleError(rawErr, { context: "fetchTogalProjects", status: response.status }),
        };
      }

      const data: TogalPaginatedResponse<TogalProject> = await response.json();
      allRows.push(...data.rows);

      if (allRows.length >= data.count || data.rows.length < TOGAL_PAGE_LIMIT) {
        break;
      }
      offset += TOGAL_PAGE_LIMIT;
    }

    return { success: true, data: allRows };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { context: "fetchTogalProjects" }),
    };
  }
};

export const fetchTogalSets = async (
  projectId: string,
  organizationId: string
): Promise<Result<TogalPaginatedResponse<TogalSet>>> => {
  try {
    const response = await togalFetch("/api/v1/set/get-sets", {
      method: "POST",
      body: JSON.stringify({
        $where: { project_id: projectId, organization_id: organizationId },
      }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const rawErr = Object.assign(new Error(errorBody.message || "Failed to fetch sets"), {
        statusCode: response.status,
      });
      return {
        success: false,
        error: handleError(rawErr, { context: "fetchTogalSets", projectId, organizationId, status: response.status }),
      };
    }

    const data: TogalPaginatedResponse<TogalSet> = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { context: "fetchTogalSets", projectId, organizationId }),
    };
  }
};

export const fetchTogalSet = async (setId: string): Promise<Result<TogalSetDetails>> => {
  try {
    const response = await togalFetch(`/api/v1/set/${setId}`);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const rawErr = Object.assign(new Error(errorBody.message || "Failed to fetch set details"), {
        statusCode: response.status,
      });
      return {
        success: false,
        error: handleError(rawErr, { context: "fetchTogalSet", setId, status: response.status }),
      };
    }

    const data: TogalSetDetails = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { context: "fetchTogalSet", setId }),
    };
  }
};

/** Views carry heavy GeoJSON payloads, chunk */
const VIEW_FETCH_LIMIT = 25;

const VIEWS_BULK_ATTRIBUTES = [
  "id",
  "updated",
  "geojson",
  "page_id",
  "user_id",
  "name",
  "set_id",
  "metadata",
  "state",
  "comparison_page_id",
  "page.prefix",
  "page.url_dpi",
  "page.metadata",
  "page.url_width",
  "page.url_height",
  "page.scale_real",
  "page.scale_type",
  "page.scale_drawing",
];

async function fetchAllPaginated<T>(
  path: string,
  whereClause: Record<string, unknown>,
  errorLabel: string,
  attributes?: string[],
  limit: number = TOGAL_PAGE_LIMIT
): Promise<Result<T[]>> {
  const allRows: T[] = [];
  let offset = 0;

  try {
    while (true) {
      const body: Record<string, unknown> = {
        $offset: offset,
        $limit: limit,
        $where: whereClause,
      };
      if (attributes) {
        body.$attributes = attributes;
      }

      const response = await togalFetch(path, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const rawErr = Object.assign(new Error(errorBody.message || "Fetch failed"), { statusCode: response.status });
        return {
          success: false,
          error: handleError(rawErr, { context: "togalPaginatedFetch", errorLabel, status: response.status }),
        };
      }

      const data: TogalPaginatedResponse<T> = await response.json();
      allRows.push(...data.rows);

      if (allRows.length >= data.count || data.rows.length < limit) {
        break;
      }
      offset += limit;
    }

    return { success: true, data: allRows };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { context: "togalPaginatedFetch", errorLabel }),
    };
  }
}

/** Bulk-fetch all classifications for a set in a single paginated POST. */
export const fetchTogalClassifications = async (
  organizationId: string,
  setId: string
): Promise<Result<TogalClassification[]>> => {
  return fetchAllPaginated<TogalClassification>(
    "/api/v1/classification/get-classifications",
    { set_id: setId, organization_id: organizationId },
    "classifications"
  );
};

export const fetchTogalPages = async (setId: string, organizationId: string): Promise<Result<TogalPage[]>> => {
  return fetchAllPaginated<TogalPage>(
    "/api/v1/page/get-pages",
    { set_id: setId, organization_id: organizationId, active: true },
    "pages"
  );
};

export const fetchTogalViewById = async (viewId: string): Promise<Result<TogalViewDetail>> => {
  try {
    const attributes = [
      "id",
      "updated",
      "filters",
      "geojson",
      "thumbnail_signed",
      "comparison_page_id",
      "comparison_translations",
      "page.prefix",
      "page.url_dpi",
      "page.metadata",
      "page.url_width",
      "page.url_height",
      "page.scale_real",
      "page.scale_type",
      "page.scale_drawing",
      "name",
      "state",
      "set_id",
      "rating",
      "message",
      "user_id",
      "page_id",
      "metadata",
      "project_id",
      "organization_id",
    ];
    const params = new URLSearchParams({ $attributes: JSON.stringify(attributes) });
    const response = await togalFetch(`/api/v1/view/${viewId}?${params}`);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const rawErr = Object.assign(new Error(errorBody.message || "Failed to fetch view"), {
        statusCode: response.status,
      });
      return {
        success: false,
        error: handleError(rawErr, { context: "fetchTogalViewById", viewId, status: response.status }),
      };
    }

    const data: TogalViewDetail = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { context: "fetchTogalViewById", viewId }),
    };
  }
};

export const aggregateClassificationQuantities = async (
  organizationId: string,
  setId: string
): Promise<
  Result<{
    classifications: TogalClassification[];
    aggregated: AggregatedQto[];
    pageCount: number;
    viewsCount: number;
  }>
> => {
  try {
    const [classificationsResult, pagesResult, viewsResult] = await Promise.all([
      fetchTogalClassifications(organizationId, setId),
      fetchTogalPages(setId, organizationId),
      fetchAllPaginated<TogalViewDetail>(
        "/api/v1/view/get-views",
        { set_id: setId, organization_id: organizationId },
        "views",
        VIEWS_BULK_ATTRIBUTES,
        VIEW_FETCH_LIMIT
      ),
    ]);

    if (!classificationsResult.success || !classificationsResult.data) {
      return {
        success: false,
        error: classificationsResult.success ? new Error("No classification data") : classificationsResult.error,
      };
    }
    if (!pagesResult.success || !pagesResult.data) {
      return { success: false, error: pagesResult.success ? new Error("No page data") : pagesResult.error };
    }
    if (!viewsResult.success || !viewsResult.data) {
      return { success: false, error: viewsResult.success ? new Error("No view data") : viewsResult.error };
    }

    const classifications = classificationsResult.data;
    const pages = pagesResult.data;
    const views = viewsResult.data;
    const activePageIds = new Set(pages.map((page) => page.id));

    type ClassAccumulator = {
      name: string;
      type: string;
      classificationCustomId: string | null;
      ediphiProductId: string | null;
      quantitySlots: ClassificationQuantitySlot[];
      slotTotals: number[];
      hasPredefinedGroupFeatures: boolean;
      hasContributingFeatures: boolean;
    };

    const classMap = new Map<string, ClassAccumulator>();
    for (const cls of classifications) {
      const quantitySlots = buildClassificationQuantitySlots(cls);
      classMap.set(cls.id, {
        name: cls.name || cls.data?.label,
        type: cls.data?.type || "unknown",
        classificationCustomId: classificationCustomOrderId(cls.data),
        ediphiProductId: ediphiProductIdFromClassificationEdiphiId(cls.data?.ediphi_product_id),
        quantitySlots,
        slotTotals: quantitySlots.map(() => 0),
        hasPredefinedGroupFeatures: false,
        hasContributingFeatures: false,
      });
    }

    for (const view of views) {
      if (!shouldIncludeViewForAggregation(view, activePageIds)) continue;

      const viewMultiplier = pickViewMultiplier(view);
      const features = view.geojson?.features ?? [];

      for (const feature of features) {
        if (!featureHasUsableGeometry(feature)) continue;

        const props = feature.properties;
        if (!props?.className) continue;

        const clsId = props.className;
        const clsInfo = classMap.get(clsId);
        if (!clsInfo || clsInfo.quantitySlots.length === 0) continue;

        if (props.predefinedGroupProps) {
          clsInfo.hasPredefinedGroupFeatures = true;
        }

        clsInfo.hasContributingFeatures = true;

        for (let slotPosition = 0; slotPosition < clsInfo.quantitySlots.length; slotPosition++) {
          const slot = clsInfo.quantitySlots[slotPosition];
          const rawQuantity = resolveFeatureQuantity(props as unknown as Record<string, unknown>, {
            unitKey: slot.unitKey,
            quantityIndex: slot.index,
            quantityKey: slot.quantityKey,
            classificationType: clsInfo.type,
            uomId: slot.uomId,
          });
          clsInfo.slotTotals[slotPosition] += rawQuantity * viewMultiplier;
        }
      }
    }

    const aggregated: AggregatedQto[] = [];

    for (const [clsId, clsInfo] of Array.from(classMap.entries())) {
      if (clsInfo.quantitySlots.length === 0) continue;

      const quantityValues: AggregatedQuantitySlot[] = clsInfo.quantitySlots.map((slot, slotPosition) => {
        let quantity = clsInfo.slotTotals[slotPosition];
        if (!slot.isCustom) {
          const family = inferMeasureFamily(slot.quantityKey, clsInfo.type);
          quantity = convertInternalQuantityToDisplayUom(quantity, slot.uomId, family);
        }
        return {
          index: slot.index,
          quantity,
          uom: slot.uom,
          quantityKey: slot.quantityKey,
        };
      });

      if (!clsInfo.hasContributingFeatures) continue;

      const primary = quantityValues[0];
      const classification = classifications.find((cls) => cls.id === clsId);
      const quantityMayBeIncomplete = classification
        ? classificationMayHaveIncompleteQuantities(classification, clsInfo.hasPredefinedGroupFeatures)
        : clsInfo.hasPredefinedGroupFeatures;

      aggregated.push({
        qtoId: clsId,
        qtoName: clsInfo.name || "Unknown",
        qtoType: clsInfo.type,
        quantity: primary.quantity,
        uom: primary.uom,
        qtoCustomId: clsInfo.classificationCustomId,
        ediphiProductId: clsInfo.ediphiProductId,
        quantities: quantityValues,
        quantityMayBeIncomplete: quantityMayBeIncomplete || undefined,
        quantityIncompleteReason: quantityMayBeIncomplete ? PREDEFINED_GROUP_INCOMPLETE_REASON : null,
      });
    }

    return {
      success: true,
      data: {
        classifications,
        aggregated,
        pageCount: pages.length,
        viewsCount: views.length,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { context: "aggregateClassificationQuantities", organizationId, setId }),
    };
  }
};
